import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useApp } from '../context/AppContext';
import { addTripStops, createLocation, getTrips } from '../services/api';
import { format } from 'date-fns';

export default function ActiveTrips() {
  const { locations, refresh } = useApp();
  const [searchParams] = useSearchParams();

  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState('');
  const [newStops, setNewStops] = useState(['']);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [quickAdd, setQuickAdd] = useState({
    openFor: null,
    name: '',
    fullAddress: '',
    saving: false,
  });

  const loadPending = useCallback(async () => {
    setLoading(true);
    try {
      const trips = await getTrips({ status: 'pending' });
      setPending(Array.isArray(trips) ? trips : []);
    } catch {
      setPending([]);
      toast.error('Could not load active trips.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPending();
  }, [loadPending]);

  useEffect(() => {
    const tripParam = searchParams.get('trip');
    if (tripParam && pending.some((t) => String(t._id) === tripParam)) {
      setSelectedId(tripParam);
    }
  }, [searchParams, pending]);

  const selected = useMemo(
    () => pending.find((t) => String(t._id) === String(selectedId)) || null,
    [pending, selectedId]
  );

  const addStopRow = () => setNewStops((s) => [...s, '']);
  const removeStopRow = (i) => setNewStops((s) => s.filter((_, idx) => idx !== i));
  const setStopRow = (i, val) => setNewStops((s) => s.map((v, idx) => (idx === i ? val : v)));

  const openQuickAdd = (stopIndex) => {
    setQuickAdd({ openFor: stopIndex, name: '', fullAddress: '', saving: false });
  };
  const cancelQuickAdd = () => {
    setQuickAdd({ openFor: null, name: '', fullAddress: '', saving: false });
  };

  const handleQuickAddSave = async () => {
    const name = quickAdd.name.trim();
    const fullAddress = quickAdd.fullAddress.trim();
    if (name.length < 2) return toast.error('Location name needs at least 2 characters.');
    if (fullAddress.length < 5) return toast.error('Full address needs at least 5 characters.');
    setQuickAdd((q) => ({ ...q, saving: true }));
    try {
      const created = await createLocation({ name, fullAddress });
      const targetIndex = quickAdd.openFor;
      await refresh();
      if (created?._id && targetIndex !== null && targetIndex !== undefined) {
        setNewStops((s) => s.map((v, idx) => (idx === targetIndex ? created._id : v)));
      }
      toast.success(`Added "${created?.name || name}".`);
      setQuickAdd({ openFor: null, name: '', fullAddress: '', saving: false });
    } catch (err) {
      const status = err?.response?.status;
      const existing = err?.response?.data?.location;
      if (status === 409 && existing?._id) {
        const targetIndex = quickAdd.openFor;
        if (targetIndex !== null && targetIndex !== undefined) {
          setNewStops((s) => s.map((v, idx) => (idx === targetIndex ? existing._id : v)));
        }
        await refresh();
        toast(`That location already exists — selected "${existing.name}".`);
        setQuickAdd({ openFor: null, name: '', fullAddress: '', saving: false });
        return;
      }
      toast.error(err?.response?.data?.message || 'Failed to add location');
      setQuickAdd((q) => ({ ...q, saving: false }));
    }
  };

  const handleSubmit = async () => {
    if (!selected) return toast.error('Select an active trip first.');
    const filled = newStops.filter(Boolean);
    if (filled.length < 1) return toast.error('Pick at least one new stop.');

    setSubmitting(true);
    try {
      const updated = await addTripStops(selected._id, {
        stops: filled,
        note: note.trim(),
      });
      toast.success(
        `Route updated — expected km is now ${updated?.expectedKM ?? '—'} km`
      );
      setNewStops(['']);
      setNote('');
      await loadPending();
      setSelectedId(String(updated._id));
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to add stops');
    } finally {
      setSubmitting(false);
    }
  };

  const routeLabel = (trip) => {
    if (!trip?.stops?.length) return '—';
    return trip.stops.map((s) => s.name).join(' → ');
  };

  return (
    <>
      <div className="page-header">
        <h2>Active Trips 📍</h2>
        <p>
          When a rider calls with extra orders, add more destinations here so expected km
          updates before they return (like inDriver).
        </p>
      </div>

      {loading ? (
        <div className="card"><p className="text-muted">Loading active trips…</p></div>
      ) : pending.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div className="icon">↗</div>
            <p>No vehicles currently out. Log a trip from Vehicle Out first.</p>
          </div>
        </div>
      ) : (
        <div className="trip-grid">
          <div className="card">
            <div className="card-title">Vehicles out ({pending.length})</div>
            <div className="trip-list">
              {pending.map((t) => (
                <div
                  key={t._id}
                  onClick={() => setSelectedId(String(t._id))}
                  className={`trip-select-item${selectedId === String(t._id) ? ' active' : ''}`}
                >
                  <div className="flex-between">
                    <div>
                      <strong>{t.vehicle?.name}</strong>
                      <span className="text-muted ml8">{t.driver?.name}</span>
                    </div>
                    <span className="mono text-sm text-muted">Out {t.outTime}</span>
                  </div>
                  <div className="text-muted text-sm mt8">
                    Expected {t.expectedKM} km · {routeLabel(t)}
                  </div>
                  {(t.routeChanges?.length ?? 0) > 0 && (
                    <span className="badge warn mt8">
                      Route updated {t.routeChanges.length}×
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="card">
            <div className="card-title">Add stops</div>
            {!selected ? (
              <p className="text-muted">Select a trip on the left.</p>
            ) : (
              <>
                <p className="text-sm text-muted mb12">
                  <strong>{selected.vehicle?.name}</strong> · {selected.driver?.name}
                  <br />
                  Current route: {routeLabel(selected)}
                </p>

                {newStops.map((stopId, i) => (
                  <div key={i} className="form-group mb12">
                    <label>New stop {i + 1}</label>
                    <div className="form-row-flex">
                      <select
                        value={stopId}
                        onChange={(e) => setStopRow(i, e.target.value)}
                      >
                        <option value="">Select location…</option>
                        {locations.map((loc) => (
                          <option key={loc._id} value={loc._id}>{loc.name}</option>
                        ))}
                      </select>
                      <button type="button" className="btn btn-sm" onClick={() => openQuickAdd(i)}>
                        + New
                      </button>
                      {newStops.length > 1 && (
                        <button type="button" className="btn btn-sm" onClick={() => removeStopRow(i)}>
                          ✕
                        </button>
                      )}
                    </div>
                    {quickAdd.openFor === i && (
                      <div className="card card-compact">
                        <div className="form-group">
                          <label>Name</label>
                          <input
                            value={quickAdd.name}
                            onChange={(e) => setQuickAdd((q) => ({ ...q, name: e.target.value }))}
                          />
                        </div>
                        <div className="form-group">
                          <label>Full address</label>
                          <input
                            value={quickAdd.fullAddress}
                            onChange={(e) => setQuickAdd((q) => ({ ...q, fullAddress: e.target.value }))}
                          />
                        </div>
                        <div className="btn-row">
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={handleQuickAddSave}
                            disabled={quickAdd.saving}
                          >
                            {quickAdd.saving ? 'Saving…' : 'Save location'}
                          </button>
                          <button type="button" className="btn btn-sm" onClick={cancelQuickAdd}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                <button type="button" className="btn btn-sm mb12" onClick={addStopRow}>
                  + Add another stop
                </button>

                <div className="form-group">
                  <label>Note (optional)</label>
                  <textarea
                    rows={2}
                    placeholder="e.g. Rider called — 2 extra deliveries"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </div>

                <button
                  type="button"
                  className="btn btn-primary w100"
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting ? 'Updating route…' : 'Append stops & recalculate km'}
                </button>

                <p className="text-muted text-sm mt12">
                  Out since {format(new Date(selected.date), 'dd MMM yyyy')} at {selected.outTime}.
                  {' '}
                  <Link to={`/trip-in`}>Vehicle In →</Link>
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
